<template>
    <v-container class="hatch-thankyou">
        <v-row>
            <v-col cols="12" md="7" class="content-col">
                <div class="heading-section">
                    <h2 class="thankyou-heading">
                        Thanks for your interest in life insurance through Symmetry.
                    </h2>
                    <div class="accent-bar"></div>
                </div>

                <div class="whats-next-section">
                    <h3 class="whats-next-title">What's next?</h3>
                    <p class="whats-next-body">
                        Based on the information you provided, we are assigning
                        <span v-if="agentName">{{ agentName }}</span>
                        <span v-else>an agent</span>
                        to help tailor the perfect coverage options for you.
                    </p>
                </div>

                <!-- OC-1404: Display calendar if agent has HatchCalendarUrl -->
                <div v-if="hasCalendar" class="calendar-section">
                    <h3 class="calendar-title">Schedule a consultation</h3>
                    <p class="calendar-body">
                        Book a time that works for you to speak with 
                        <span v-if="agentName">{{ agentName }}</span>
                        <span v-else>your agent</span>:
                    </p>
                    <div class="calendar-embed">
                        <iframe
                            :src="calendarUrl"
                            width="100%"
                            height="700"
                            frameborder="0"
                        ></iframe>
                    </div>
                </div>

                <div class="steps-box">
                    <v-row class="steps-row">
                        <v-col cols="12" sm="6">
                            <div class="step-item">
                                <img
                                    :src="require('../assets/hatch-thankyou-step1.svg')"
                                    alt="Step 1"
                                    class="step-icon"
                                />
                                <div class="step-content">
                                    <h4 class="step-title">
                                        Connect with
                                        <span v-if="agentName">{{ agentName }}</span>
                                        <span v-else>your agent</span>
                                    </h4>
                                    <p class="step-description">
                                        Your agent will call you to schedule an appointment to review your policy options.
                                    </p>
                                </div>
                            </div>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <div class="step-item">
                                <img
                                    :src="require('../assets/hatch-thankyou-step2.svg')"
                                    alt="Step 2"
                                    class="step-icon"
                                />
                                <div class="step-content">
                                    <h4 class="step-title">Get coverage!</h4>
                                    <p class="step-description">
                                        You'll secure the life insurance coverage that best suits your needs.
                                    </p>
                                </div>
                            </div>
                        </v-col>
                    </v-row>
                </div>
            </v-col>

            <v-col cols="5" class="hero-col d-none d-md-flex">
                <div class="hero-image-wrapper">
                    <div class="hero-circle-bg"></div>
                    <div class="hero-image-mask">
                        <img
                            :src="require('../assets/hatch-thankyou-hero.png')"
                            alt=""
                            class="hero-image"
                        />
                    </div>
                </div>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
export default {
    name: "HatchThankyou",
    computed: {
        agent() {
            return this.$store.state.agent
        },
        agentName() {
            return this.agent.agentPreferredName || null
        },
        // OC-1404: Calendar display logic
        hasCalendar() {
            return !!(this.agent.HatchCalendarUrl && this.agent.HatchCalendarUrl.trim() !== '')
        },
        calendarUrl() {
            return this.hasCalendar ? this.agent.HatchCalendarUrl : ''
        }
    },
    mounted() {
        window.scrollTo(0, 0)
    }
}
</script>

<style scoped>
.hatch-thankyou {
    font-family: "Montserrat", sans-serif;
    padding-top: 40px;
    padding-bottom: 60px;
}

.content-col {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

/* Heading */
.thankyou-heading {
    font-weight: 800;
    font-size: 35px;
    line-height: 49px;
    color: #222;
    margin: 0;
    max-width: 600px;
}

.accent-bar {
    width: 60px;
    height: 5px;
    background-color: #00B7FF;
    margin-top: 10px;
}

/* What's Next */
.whats-next-section {
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-top: 30px;
}

.whats-next-title {
    font-weight: 700;
    font-size: 25px;
    line-height: 35px;
    color: #222;
    letter-spacing: 0.125px;
    margin: 0;
}

.whats-next-body {
    font-weight: 300;
    font-size: 20px;
    line-height: 30px;
    color: #222;
    letter-spacing: 0.125px;
    margin: 0;
    max-width: 684px;
}

/* OC-1404: Calendar Section */
.calendar-section {
    display: flex;
    flex-direction: column;
    gap: 15px;
    margin-top: 30px;
}

.calendar-title {
    font-weight: 700;
    font-size: 25px;
    line-height: 35px;
    color: #222;
    letter-spacing: 0.125px;
    margin: 0;
}

.calendar-body {
    font-weight: 300;
    font-size: 20px;
    line-height: 30px;
    color: #222;
    letter-spacing: 0.125px;
    margin: 0;
}

.calendar-embed {
    width: 100%;
    max-width: 800px;
    margin-top: 10px;
    border: 2px solid rgba(0, 183, 255, 0.3);
    border-radius: 10px;
    overflow: hidden;
}

/* Steps Box */
.steps-box {
    border: 3px solid rgba(0, 183, 255, 0.6);
    border-radius: 15px;
    padding: 30px;
    margin-top: 10px;
}

.steps-box::after {
    content: none;
}

.steps-row {
    align-items: flex-start;
}

.step-item {
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.step-icon {
    width: 40px;
    height: 40px;
}

.step-content {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.step-title {
    font-weight: 700;
    font-size: 20px;
    line-height: 25.5px;
    color: #222;
    letter-spacing: 0.125px;
    margin: 0;
}

.step-description {
    font-weight: 300;
    font-size: 20px;
    line-height: 30px;
    color: #222;
    letter-spacing: 0.125px;
    margin: 0;
}

/* Hero Image */
.hero-col {
    align-items: flex-start;
    justify-content: center;
    padding-top: 40px;
}

.hero-image-wrapper {
    position: relative;
    width: 100%;
    max-width: 469px;
    aspect-ratio: 1;
}

.hero-circle-bg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background-color: #00B7FF;
    opacity: 0.33;
}

.hero-image-mask {
    position: relative;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    overflow: hidden;
}

.hero-image {
    width: 103.55%;
    height: 138.09%;
    object-fit: cover;
    object-position: center top;
    position: absolute;
    top: -4.61%;
    left: -1.78%;
}

/* Mobile Responsive Styles */
@media only screen and (max-width: 959px) {
    .hatch-thankyou {
        padding-top: 20px;
        padding-bottom: 40px;
    }

    .thankyou-heading {
        font-size: 24px;
        line-height: 33.6px;
    }

    .whats-next-title {
        font-size: 18px;
        line-height: 25.2px;
    }

    .whats-next-body {
        font-size: 17px;
        line-height: 25.5px;
    }

    .step-title {
        font-size: 17px;
        line-height: 22.5px;
    }

    .step-description {
        font-size: 17px;
        line-height: 25.5px;
    }

    .steps-box {
        padding: 20px;
    }

    .step-item {
        gap: 15px;
    }
}
</style>
